/**
 * 财务配置和付费节点类型定义
 */

// 付费模式枚举
export enum PaymentMode {
  FIXED = 'FIXED',              // 固定收费
  MILESTONE = 'MILESTONE',      // 节点付费
  RISK_AGENCY = 'RISK_AGENCY',  // 风险代理
  HOURLY = 'HOURLY',            // 按小时计费
}

// 付费节点状态枚举
export enum MilestoneStatus {
  PENDING = 'PENDING',      // 待付款
  PAID = 'PAID',            // 已付款
  OVERDUE = 'OVERDUE',      // 已逾期
  CANCELLED = 'CANCELLED',  // 已取消
}

// 案件财务配置接口
export interface CaseFinanceConfig {
  id: number;
  case_id: number;
  payment_mode: PaymentMode;
  contract_amount: number;
  
  // 风险代理相关字段
  risk_base_fee?: number;
  risk_success_rate?: number;
  risk_target_amount?: number;
  risk_payment_condition?: string;
  risk_min_fee?: number;
  risk_max_fee?: number;
  
  // 按小时计费相关字段
  hourly_rate?: number;
  estimated_hours?: number;
  
  notes?: string;
  created_by: number;
  created_at: string;
  updated_at: string;
}

// 付费节点接口
export interface PaymentMilestone {
  id: number;
  case_id: number;
  milestone_name: string;
  milestone_order: number;
  payment_amount: number;
  payment_ratio?: number;
  trigger_condition?: string;
  due_date?: string;
  status: MilestoneStatus;
  actual_paid_amount?: number;
  paid_date?: string;
  notes?: string;
  created_by: number;
  created_at: string;
  updated_at: string;
  
  // 计算字段
  remaining_amount?: number;
  payment_progress?: number;
}

// 带财务记录的付费节点
export interface PaymentMilestoneWithRecords extends PaymentMilestone {
  finance_records_count: number;
  finance_records_total: number;
}

// 付费节点进度统计
export interface PaymentMilestoneProgress {
  total_milestones: number;
  pending_milestones: number;
  paid_milestones: number;
  overdue_milestones: number;
  total_amount: number;
  paid_amount: number;
  remaining_amount: number;
  payment_progress: number;
}

// 完整的案件财务配置（包含付费节点）
export interface CaseFinanceFullConfig extends CaseFinanceConfig {
  milestones: PaymentMilestone[];
  milestone_progress?: PaymentMilestoneProgress;
}

// 创建案件财务配置请求
export interface CaseFinanceConfigCreate {
  case_id: number;
  payment_mode: PaymentMode;
  contract_amount: number;
  risk_base_fee?: number;
  risk_success_rate?: number;
  risk_target_amount?: number;
  risk_payment_condition?: string;
  risk_min_fee?: number;
  risk_max_fee?: number;
  hourly_rate?: number;
  estimated_hours?: number;
  notes?: string;
}

// 更新案件财务配置请求
export interface CaseFinanceConfigUpdate {
  payment_mode?: PaymentMode;
  contract_amount?: number;
  risk_base_fee?: number;
  risk_success_rate?: number;
  risk_target_amount?: number;
  risk_payment_condition?: string;
  risk_min_fee?: number;
  risk_max_fee?: number;
  hourly_rate?: number;
  estimated_hours?: number;
  notes?: string;
}

// 创建付费节点请求
export interface PaymentMilestoneCreate {
  case_id: number;
  milestone_name: string;
  milestone_order: number;
  payment_amount: number;
  payment_ratio?: number;
  trigger_condition?: string;
  due_date?: string;
  notes?: string;
}

// 更新付费节点请求
export interface PaymentMilestoneUpdate {
  milestone_name?: string;
  milestone_order?: number;
  payment_amount?: number;
  payment_ratio?: number;
  trigger_condition?: string;
  due_date?: string;
  status?: MilestoneStatus;
  notes?: string;
}

// 批量创建付费节点请求
export interface PaymentMilestoneBatchCreate {
  case_id: number;
  milestones: Omit<PaymentMilestoneCreate, 'case_id'>[];
}

// ==================== 线索财务配置类型 ====================

// 线索财务配置接口
export interface LeadFinanceConfig {
  id: number;
  lead_id: number;
  entity_type: 'LEAD';
  entity_id: number;
  payment_mode: PaymentMode;
  contract_amount: number;
  
  // 风险代理相关字段
  risk_base_fee?: number;
  risk_success_rate?: number;
  risk_target_amount?: number;
  risk_payment_condition?: string;
  risk_min_fee?: number;
  risk_max_fee?: number;
  
  // 按小时计费相关字段
  hourly_rate?: number;
  estimated_hours?: number;
  
  notes?: string;
  created_by: number;
  created_at: string;
  updated_at: string;
}

// 完整的线索财务配置（包含付费节点）
export interface LeadFinanceFullConfig extends LeadFinanceConfig {
  milestones: PaymentMilestone[];
  milestone_progress?: PaymentMilestoneProgress;
}

// 创建线索财务配置请求
export interface LeadFinanceConfigCreate {
  lead_id: number;
  payment_mode: PaymentMode;
  contract_amount: number;
  risk_base_fee?: number;
  risk_success_rate?: number;
  risk_target_amount?: number;
  risk_payment_condition?: string;
  risk_min_fee?: number;
  risk_max_fee?: number;
  hourly_rate?: number;
  estimated_hours?: number;
  notes?: string;
}

// 更新线索财务配置请求
export interface LeadFinanceConfigUpdate {
  payment_mode?: PaymentMode;
  contract_amount?: number;
  risk_base_fee?: number;
  risk_success_rate?: number;
  risk_target_amount?: number;
  risk_payment_condition?: string;
  risk_min_fee?: number;
  risk_max_fee?: number;
  hourly_rate?: number;
  estimated_hours?: number;
  notes?: string;
}

// 创建线索付费节点请求
export interface LeadPaymentMilestoneCreate {
  lead_id: number;
  milestone_name: string;
  milestone_order: number;
  payment_amount: number;
  payment_ratio?: number;
  trigger_condition?: string;
  due_date?: string;
  notes?: string;
}

// 批量创建线索付费节点请求
export interface LeadPaymentMilestoneBatchCreate {
  lead_id: number;
  milestones: Omit<LeadPaymentMilestoneCreate, 'lead_id'>[];
}
